import { useContext, useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Input,
  makeStyles,
  Tooltip,
} from '@fluentui/react-components';
import { Dismiss24Regular, SearchRegular } from '@fluentui/react-icons';

import { LiteratureSummary, OpenLiteratureButton, TLiterature, useLiteratures } from '@modules/literatures';
import { EAssessmentDecision } from '@modules/literatures/types';
import { SignInContext } from '@modules/users';
import { useOneAtATime } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import DocumentContext from '../../contexts/DocumentContext.context';
import { TInsertLiteratureCitationDialogState } from '../../types/internal.types';
import { TResolvedLiteratureCitation } from '../../types/reference.types';
import getDocumentProductId from '../../helpers/getDocumentProductId';
import { useAddLiteratureCitation } from '../../api';

const L = TERMINOLOGY.literature;

type TProps = {
  state?: TInsertLiteratureCitationDialogState;
  setState: (state: TInsertLiteratureCitationDialogState | undefined) => void;
};

const SEARCH_DEBOUNCE_MS = 250;
const PAGE_SIZE = 25;

const useStyles = makeStyles({
  content: {
    maxHeight: '60vh',
    overflowY: 'auto',
  },
  actions: {
    gridColumnStart: 1,
  },
});

const InsertLiteratureCitationDialog = ({ state, setState }: TProps) => {
  const classes = useStyles();
  const { doc, editor } = useContext(DocumentContext);
  const { organization } = useContext(SignInContext);
  const addCitation = useAddLiteratureCitation();
  const productId = getDocumentProductId(doc, organization);

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  // Reset on open so a stale filter from a previous session doesn't show.
  useEffect(() => {
    if (state?.isOpen) {
      setSearchInput('');
      setDebouncedSearch('');
    }
  }, [state?.isOpen]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useLiteratures({
    page: 1,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    productIds: productId ? [productId] : undefined,
    decision: productId ? EAssessmentDecision.INCLUDED : undefined,
    enabled: !!state?.isOpen,
  });

  const items = data?.items ?? [];

  const handleClose = () => {
    if (state) setState({ ...state, isOpen: false });
  };

  const handleSelect = useOneAtATime(async (item: TLiterature) => {
    let statusEntry: TResolvedLiteratureCitation;
    try {
      statusEntry = await addCitation.mutateAsync({ doc, literature: item });
    } catch {
      return;
    }
    editor.chain().focus().insertLiteratureCitation(item._id, statusEntry).run();
    if (state) setState({ ...state, isOpen: false });
  });

  return (
    <Dialog
      open={state?.isOpen || false}
      onOpenChange={(_, data) => {
        if (!data.open) handleClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button appearance="subtle" aria-label="close" icon={<Dismiss24Regular />} />
              </DialogTrigger>
            }
          >
            Insert {L.lower} citation
          </DialogTitle>
          <DialogContent className={classes.content}>
            <Input
              className="w-full mb-3"
              value={searchInput}
              onChange={(_, d) => setSearchInput(d.value)}
              placeholder="Title, author, reference number, DOI..."
              contentBefore={<SearchRegular />}
              autoFocus
            />

            {isLoading ? (
              <p className="text-neutral-500 py-2">Loading {L.lower}...</p>
            ) : items.length === 0 ? (
              <p className="text-neutral-500 py-2">No included {L.lower} found for this product.</p>
            ) : (
              items.map((item) => {
                const hasVersion = item.versions.length > 0;
                const row = (
                  <div
                    key={item._id}
                    className={`flex justify-between items-center gap-2 py-2 cursor-pointer rounded hover:bg-neutral-100 has-[a:hover]:!bg-transparent ${
                      hasVersion ? '' : 'opacity-55 cursor-not-allowed hover:bg-transparent'
                    }`}
                    role="button"
                    tabIndex={hasVersion ? 0 : -1}
                    onClick={() => hasVersion && handleSelect(item)}
                    onKeyDown={(e) => {
                      if (hasVersion && (e.key === 'Enter' || e.key === ' ')) handleSelect(item);
                    }}
                  >
                    <LiteratureSummary content={item.draft?.content ?? item.versions[0]?.content} />
                    <Tooltip
                      content={`Open ${L.lower} in a new tab`}
                      relationship="label"
                      withArrow
                      positioning="after"
                    >
                      <OpenLiteratureButton
                        literatureId={item._id}
                        appearance="transparent"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </Tooltip>
                  </div>
                );
                return hasVersion ? (
                  row
                ) : (
                  <Tooltip
                    key={item._id}
                    content="Draft only – complete a version to cite."
                    relationship="description"
                    withArrow
                    positioning="after"
                  >
                    {row}
                  </Tooltip>
                );
              })
            )}
          </DialogContent>
          <DialogActions className={classes.actions}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary">Cancel</Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};

export default InsertLiteratureCitationDialog;
